import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { MediaAsset, formatSize } from "./types";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Check, Eye, Copy, Trash2, FileText, MoreVertical, ImageIcon
} from "lucide-react";

interface MediaGridProps {
  assets: MediaAsset[];
  viewMode: "grid" | "list";
  onSelect: (id: string) => void;
  onPreview: (asset: MediaAsset) => void;
  onCopyUrl: (url: string) => void;
  onEditAlt: (asset: MediaAsset) => void;
  onDelete: (asset: MediaAsset) => void;
}

export default function MediaGrid({
  assets,
  viewMode,
  onSelect,
  onPreview,
  onCopyUrl,
  onEditAlt,
  onDelete,
}: MediaGridProps) {
  const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());

  const handleImageError = useCallback((asset: MediaAsset) => {
    setBrokenIds((prev) => {
      if (prev.has(asset.id)) return prev;
      const next = new Set(prev);
      next.add(asset.id);
      // Fire-and-forget cleanup from DB
      supabase.from("media_assets").delete().eq("id", asset.id).then(() => {});
      return next;
    });
  }, []);

  const visibleAssets = assets.filter((a) => !brokenIds.has(a.id));

  if (visibleAssets.length === 0) {
    return (
      <div className="text-center py-16">
        <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground">No files found</p>
      </div>
    );
  }

  if (viewMode === "list") {
    return (
      <div className="space-y-1.5">
        {visibleAssets.map((asset) => (
          <div
            key={asset.id}
            className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all hover:bg-accent/50 ${
              asset.selected ? "border-primary bg-primary/5" : "border-border"
            }`}
          >
            <button
              onClick={() => onSelect(asset.id)}
              className={`h-5 w-5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                asset.selected ? "bg-primary border-primary" : "border-muted-foreground/30"
              }`}
            >
              {asset.selected && <Check className="h-3 w-3 text-primary-foreground" />}
            </button>
            <div className="h-10 w-10 rounded overflow-hidden bg-muted flex-shrink-0 cursor-pointer" onClick={() => onPreview(asset)}>
              {asset.mime_type.startsWith("image/") ? (
                <img src={asset.file_url} alt={asset.alt_text || asset.file_name} loading="lazy" decoding="async" className="h-full w-full object-cover" onError={() => handleImageError(asset)} />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                  <FileText className="h-4 w-4" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-foreground">{asset.file_name}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{formatSize(asset.file_size)}</span>
                {asset.tags && asset.tags.length > 0 && (
                  <div className="flex gap-1">
                    {asset.tags.slice(0, 3).map((t) => (
                      <Badge key={t} variant="outline" className="text-[9px] px-1 py-0">{t}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <span className="text-xs text-muted-foreground flex-shrink-0">
              {new Date(asset.created_at).toLocaleDateString()}
            </span>
            <AssetMenu asset={asset} onPreview={onPreview} onCopyUrl={onCopyUrl} onEditAlt={onEditAlt} onDelete={onDelete} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {visibleAssets.map((asset) => (
        <div
          key={asset.id}
          className={`group relative rounded-xl overflow-hidden border transition-all hover:shadow-md ${
            asset.selected ? "border-primary ring-2 ring-primary/20" : "border-border"
          }`}
        >
          <button
            onClick={() => onSelect(asset.id)}
            className={`absolute top-2 left-2 z-10 h-6 w-6 rounded-md border flex items-center justify-center transition-all ${
              asset.selected
                ? "bg-primary border-primary"
                : "bg-background/80 border-border opacity-0 group-hover:opacity-100"
            }`}
          >
            {asset.selected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
          </button>
          <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
            <AssetMenu asset={asset} onPreview={onPreview} onCopyUrl={onCopyUrl} onEditAlt={onEditAlt} onDelete={onDelete} />
          </div>
          <div className="aspect-[4/3] bg-muted relative overflow-hidden cursor-pointer" onClick={() => onPreview(asset)}>
            {asset.mime_type.startsWith("image/") ? (
              <img src={asset.file_url} alt={asset.alt_text || asset.file_name} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" decoding="async" onError={() => handleImageError(asset)} />
            ) : (
              <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <FileText className="h-6 w-6" />
                <span className="text-xs uppercase">{asset.mime_type.split("/")[1]}</span>
              </div>
            )}
            {asset.source && asset.source !== "upload" && (
              <Badge variant="secondary" className="absolute bottom-1 left-1 text-[9px] px-1 h-4 capitalize opacity-90">
                {asset.source}
              </Badge>
            )}
          </div>
          <div className="p-2.5 space-y-1 bg-card">
            <p className="text-xs font-medium truncate text-foreground">{asset.file_name}</p>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">{formatSize(asset.file_size)}</span>
              {asset.width && asset.height && (
                <span className="text-[10px] text-muted-foreground">{asset.width}×{asset.height}</span>
              )}
            </div>
            {asset.tags && asset.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-0.5">
                {asset.tags.slice(0, 2).map((t) => (
                  <Badge key={t} variant="outline" className="text-[9px] px-1 py-0">{t}</Badge>
                ))}
                {asset.tags.length > 2 && (
                  <span className="text-[9px] text-muted-foreground">+{asset.tags.length - 2}</span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AssetMenu({
  asset,
  onPreview,
  onCopyUrl,
  onEditAlt,
  onDelete,
}: {
  asset: MediaAsset;
  onPreview: (a: MediaAsset) => void;
  onCopyUrl: (url: string) => void;
  onEditAlt: (a: MediaAsset) => void;
  onDelete: (a: MediaAsset) => void;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton variant="ghost" className="h-7 w-7 bg-background/80 hover:bg-background" label={t("actions.more")}>
          <MoreVertical className="h-3.5 w-3.5" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={() => onPreview(asset)}><Eye className="h-4 w-4 mr-2" />Preview</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onCopyUrl(asset.file_url)}><Copy className="h-4 w-4 mr-2" />Copy URL</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEditAlt(asset)}><FileText className="h-4 w-4 mr-2" />Alt Text</DropdownMenuItem>
        <DropdownMenuItem className="text-destructive" onClick={() => onDelete(asset)}>
          <Trash2 className="h-4 w-4 mr-2" />Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
